// External Library Imports
import React, { useState, useEffect, useCallback } from "react";
import Axios from "axios";
import { Lightbox } from "react-modal-image";
import { shallowEqual } from "react-redux";

// React-Bootstrap Component Imports
import { Row, Col } from "react-bootstrap";

// Redux and Services Imports
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { useMediaQuery } from "../../../../hooks/useMediaQuery";
import { useHotels } from "../../../../hooks/useHotels";
import {
  searchSelector,
  setSearch,
  addAmenity,
  deleteAmenity,
  addBrand,
  deleteBrand,
} from "../../../../redux/slices/Search/search";
import {
  loadHotels,
  hotelsSelector,
  loadInterestsHotels,
} from "../../../../redux/slices/Hotel/hotel";
import { brandSelector } from "../../../../redux/slices/Brand/brand";
import {
  clearRates,
  selectHotelRates,
} from "../../../../redux/slices/Rate/rate";
import { fetchRoomsAndRatesASE } from "../../../../services/rates";

// Utility and Helper Imports
import { addProductImpressionGTMDataLayer } from "../../../../utils/datalayers";
import { Storage } from "../../../../utils/storage";
import { Constants } from "../../../../@types/Constants";

// Local Component Imports
import SearchList from "../SearchList/SearchList";
import SearchMap from "../SearchMap/SearchMap";
import UnlockBanner from "../../UnlockBanner/UnlockBanner";

// Type, Interface, and Styled Component Imports
import { HotelProximityInfo, ISearchResultProps } from "./SearchResultProps";
import {
  SearchContainer,
  LocationAccessPrompt,
  NoHotelsFound,
  PromoAlert,
  convertArrayToObject,
} from "./SearchResultHelpers";
import SearchHeader from "./helperComponents/SearchHeader/SearchHeader";
import SearchFilter from "./helperComponents/SearchFilter/SearchFilter";

import {
  sortHotelsByRate,
  sortAndFilterByBrand,
  sortHotelsByDistance,
  filterBrandBiasHotels,
  getQueryStringBrands,
  revelanceSortHotel,
} from "./helperComponents/utils";

const SearchResult: React.FC<ISearchResultProps> = (props) => {
  const dispatch: any = useAppDispatch();
  const allHotels = useHotels();
  const allHotelsByCrsCode = convertArrayToObject(allHotels, "crs_code");
  const hotelsRates = useAppSelector(
    (state) => state.rates && state.rates,
    shallowEqual
  );
  const [sortBy, setSortBy] = useState(props.sortBy || "relevance");
  const [sortOrder, setSortOrder] = useState("asc");
  const [distanceFilter, setDistanceFilter] = useState("");
  const [display, setDisplay] = useState("list");
  const hotelReferences =
    props && props?.hotelReferences ? props.hotelReferences : [];
  const [hotelCodes, setHotelCodes] = useState([]);
  const [showHotelZoomedImage, setShowHotelZoomedImage] = useState(false);
  const [isDataLayer, setIsDataLayer] = useState(false);
  const [hotelImageForZoom, setHotelImageForZoom] = useState({
    url: "",
    alt: "",
  });
  const brandBias = Storage.GetLocalStorageValue(Constants.BRAND_BIAS_KEY);
  const search = useAppSelector(searchSelector);
  const { hotels, isLoading } = useAppSelector(hotelsSelector, shallowEqual);
  const rates = useAppSelector(selectHotelRates(hotelCodes));
  const brand = useAppSelector(brandSelector, shallowEqual);
  const hotelSearchFilters = props.hotelSearchFilters?.length
    ? props.hotelSearchFilters
    : [];
  const [shouldDisplayPromoMessage, setShouldDisplayPromoMessage] =
    useState(false);
  const [hotelsRateLoadedCount, setHotelsRateLoadedCount] = useState(0);
  const isLoggedIn = useAppSelector(
    (state) => state.member.isLoggedIn,
    shallowEqual
  );
  const hrDiscountUnlockedInStore = useAppSelector(
    (state) => state.show_special_pricing,
    shallowEqual
  );
  const hrDiscountUnlockedInStorage = Storage.GetLocalStorageValue(
    "HR-discount-unlocked"
  );
  const discountUnlocked =
    isLoggedIn || hrDiscountUnlockedInStore || hrDiscountUnlockedInStorage;
  const isMobile = useMediaQuery("(max-width: 991px)");

  //Checking for room rates
  useEffect(() => {
    if (hotelsRates !== null) {
      const hotel = hotelsRates && Object.keys(hotelsRates);
      const hoteldetails = hotel.length && hotelsRates[hotel[0]];
      if (!hoteldetails?.Rooms?.length) {
        dispatch(clearRates({}));
      }
    }
  }, []);

  useEffect(() => {
    const source = Axios.CancelToken.source();
    let didCancel = false;

    async function loadHotelsAsync() {
      setShouldDisplayPromoMessage(false);
      setHotelsRateLoadedCount(0);
      setIsDataLayer(false);
      const { distance, latitude, longitude, searchTypeGapi } = search;
      if (props.interestSearch) {
        await dispatch(
          loadInterestsHotels(
            props.distance || distance,
            latitude,
            longitude,
            31,
            props.interestURL,
            source,
            hotelReferences
          )
        );
      } else {
        await dispatch(
          loadHotels(
            allHotelsByCrsCode,
            props.distance || distance,
            latitude,
            longitude,
            31,
            source,
            hotelReferences,
            brandBias ? null : searchTypeGapi,
            search?.searchString?.split(",")[0].trim()
          )
        );
      }

      if (!didCancel) {
        setIsDataLayer(true);
      }
    }

    loadHotelsAsync();

    return () => {
      didCancel = true;
      source.cancel("Request cancelled for hotel search.");
    };
  }, [search]);

  useEffect(() => {
    let didCancel = false;
    if (!didCancel) {
      if (hotels) {
        const brandsArray = getQueryStringBrands(
          props?.location?.state?.queryString
        );
        const brandFilteredHotels =
          brandsArray.length > 0
            ? Object.keys(hotels).filter((crsCode) => {
                const hotel = hotels[crsCode];
                return (
                  hotel.brand.code && brandsArray.includes(hotel.brand.code)
                );
              })
            : Object.keys(hotels);
        setHotelCodes(brandFilteredHotels);
      } else {
        setHotelCodes([]);
      }
      if (
        !isLoading &&
        hotels &&
        !Object.keys(hotels).length &&
        sortBy === "distance" &&
        distanceRadius >= 25 &&
        distanceRadius < 50 &&
        !distanceFilter
      ) {
        dispatch(setSearch({ distance: distanceRadius + 25 }));
      }
    }
    return () => {
      didCancel = true;
    };
  }, [hotels, isLoading]);

  useEffect(() => {
    props.sortBy && props.sortBy == "brand" && setSortBy("brand");
  }, [props.sortBy]);

  useEffect(() => {
    if (
      hotelsRateLoadedCount > 0 &&
      hotelsRateLoadedCount == hotelList.length &&
      !!(search.promotionCode || search.groupCode)
    ) {
      setShouldDisplayPromoMessage(true);
    }
  }, [hotelsRateLoadedCount]);

  const handlePriceChange = useCallback((priceFilter: string) => {
    dispatch(setSearch({ priceFilter: priceFilter }));
  }, []);
  const handleDistanceChange = useCallback((distanceFilter: number) => {
    setDistanceFilter(distanceFilter);
    dispatch(setSearch({ distance: distanceFilter }));
  }, []);
  const handleAmenitiesChange = useCallback((amenity: any, method: string) => {
    if (method === "add") {
      dispatch(addAmenity(amenity));
    }
    if (method === "delete") {
      dispatch(deleteAmenity(amenity));
    }
  }, []);
  const handleBrandsChange = useCallback((brand: any, method: string) => {
    if (method === "add") {
      dispatch(addBrand(brand));
    }
    if (method === "delete") {
      dispatch(deleteBrand(brand));
    }
  }, []);
  const handleSortChange = useCallback((sortBy: string, sortOrder: string) => {
    setSortBy(sortBy);
    setSortOrder(sortOrder);
  }, []);
  const handleSetDisplay = useCallback(
    (view: string) => {
      if (display !== view) {
        setDisplay(view);
      }
    },
    [display]
  );

  const handleClickHotelImage = useCallback(
    (imgUrl: string, altText: string) => {
      setHotelImageForZoom({
        url: imgUrl,
        alt: altText,
      });
      setShowHotelZoomedImage(true);
    },
    []
  );
  const handlePromoMessageAlert = useCallback(() => {
    setShouldDisplayPromoMessage(false);
    setHotelsRateLoadedCount(0);
  }, []);
  const onRateLoad = useCallback(() => {
    setHotelsRateLoadedCount(hotelsRateLoadedCount + 1);
  }, [hotelsRateLoadedCount]);

  const { distance, amenities, brands, searchString } = search;
  const distanceRadius = props.distance || distance || 25;

  let hotelList: HotelProximityInfo[] | [] = [];
  const crsCodes: string[] = [];
  const crsCodesBrands: string[] = [];
  let priceCount = 0;
  let isPromoValid: boolean | null = null;
  if (hotelCodes.length) {
    isPromoValid = null;
    Object.keys(hotels).forEach(function (crsCode) {
      let hotel = hotels[crsCode];
      const hotelAmenities = allHotels.find(
        (hotelInfo: any) => crsCode === hotelInfo.crs_code
      )?.hotelCardAmenities;

      let isThisHotelPromoValid = null;
      const miles = parseInt(hotel.rangeInMilesFromOrigin);

      if (miles <= distanceRadius || isNaN(miles)) {
        const amenityValues = Object.keys(amenities);
        const brandValues = Object.values(brands);
        const rate =
          hotel.crsHotelCode in rates ? rates[hotel.crsHotelCode] : null; //rates[crsCode] || null;
        const redemptionItem = rate?.redemptionItem;
        let price = null;
        let bestRateCode = null;
        let baseRate = null;
        let fromRateType = null;
        let hotelCurrency;
        let roomCount = 0;
        if (rate?.Rooms) {
          const parsedRooms = fetchRoomsAndRatesASE(
            rate.Rooms,
            search.discount,
            search.promotionCode,
            search.groupCode,
            search.checkin,
            search.checkout,
            redemptionItem
          );
          if (parsedRooms !== null) {
            roomCount = parsedRooms.filter(
              (room) => room.accessible === false
            ).length;

            // If no non-accessible rooms, count accessible rooms
            roomCount =
              roomCount ||
              parsedRooms.filter((room) => room.accessible === true).length;
          }

          if (parsedRooms !== null) {
            price = parsedRooms[0]?.FromRateWithFees;
            bestRateCode = parsedRooms[0].FromRateCode;
            baseRate = parsedRooms[0]?.BaseRateWithFees;
            fromRateType = parsedRooms[0].FromRateType;
            if (search.promotionCode || search.groupCode) {
              isThisHotelPromoValid = !!parsedRooms[0].FromRatePromotional;
              //if promo is valid for atleast one hotel then don't check again
              if (!isPromoValid) {
                isPromoValid = isThisHotelPromoValid;
              }
            }
            hotelCurrency = parsedRooms.length
              ? parsedRooms[0].Currency
              : "USD";
          }
        }
        const hotelBestPrice = price !== null ? parseFloat(price) : null;
        const hotelBaseRate = baseRate !== null ? parseFloat(baseRate) : null;
        const showUnlockButton = fromRateType === "member" && !isLoggedIn;
        const priceLocked = showUnlockButton && !discountUnlocked;
        const showMemberRateLabel =
          process.env.GATSBY_SHOW_MEMEBER_RATE_LABEL === "true"
            ? fromRateType === "member"
            : false;
        hotel = {
          ...hotel,
          bestPrice: hotelBestPrice,
          bestRateCode: bestRateCode,
          baseRate: hotelBaseRate,
          displayPrice: showMemberRateLabel
            ? hotelBestPrice
            : priceLocked
            ? hotelBaseRate
            : hotelBestPrice,
          IsPromotionValid: isThisHotelPromoValid,
          currency: hotelCurrency,
          displayRoomCount: roomCount,
        };
        let matched = true;
        if (amenityValues.length) {
          if (hotelAmenities?.length) {
            matched = amenityValues.every((amenity) => {
              return hotelAmenities.find(
                (hotel_amenity: any) => hotel_amenity.machine_name === amenity
              );
            });
          } else {
            matched = false;
          }
        }

        matched &&
          hotel.crsHotelCode &&
          crsCodesBrands.push(hotel.crsHotelCode);
        if (brandValues.length) {
          matched = matched && brandValues.includes(hotel.brand.code);
        }
        if (matched) {
          hotelList.push(hotel);

          hotel.crsHotelCode && crsCodes.push(hotel.crsHotelCode);
          if (rate !== null) {
            priceCount++;
          }
        }
      }
    });
  }
  if (search.searchType === "locality" || search.searchType === "city") {
    hotelList = revelanceSortHotel(hotelList, searchString);
  }
  const hotelCount = hotelList.length;
  const crsCodesString = crsCodes.length ? crsCodes.toString() : "";
  const crsCodesBrandsString = crsCodesBrands.length
    ? crsCodesBrands.toString()
    : null;
  if (hotelCount) {
    const brandBiasData = JSON.parse(brandBias) as {
      brand_code?: string;
    };
    switch (sortBy) {
      case "price":
        if (priceCount === hotelCount) {
          const rateKeyForSort = "displayPrice";
          if (brandBias && brandBiasData?.brand_code) {
            const [brandBiasHotels, nonBrandBiasHotels] =
              filterBrandBiasHotels(hotelList);
            hotelList = [
              ...sortHotelsByRate(brandBiasHotels, rateKeyForSort, sortOrder),
              ...sortHotelsByRate(
                nonBrandBiasHotels,
                rateKeyForSort,
                sortOrder
              ),
            ];
          } else {
            hotelList = sortHotelsByRate(hotelList, rateKeyForSort, sortOrder);
          }
        }
        break;
      case "brand":
        if (brand?.brandCode || brand?.brand_code) {
          if (brandBias && brandBiasData?.brand_code) {
            const [brandBiasHotels, nonBrandBiasHotels] =
              filterBrandBiasHotels(hotelList);
            hotelList = [
              ...sortAndFilterByBrand(brandBiasHotels, brand),
              ...sortAndFilterByBrand(nonBrandBiasHotels, brand),
            ];
          } else {
            hotelList = sortAndFilterByBrand(hotelList, brand);
          }
        }
        break;
      case "distance":
        if (brandBias && brandBiasData?.brand_code) {
          const [brandBiasHotels, nonBrandBiasHotels] =
            filterBrandBiasHotels(hotelList);
          hotelList = [
            ...sortHotelsByDistance(brandBiasHotels, sortOrder),
            ...sortHotelsByDistance(nonBrandBiasHotels, sortOrder),
          ];
        } else {
          hotelList = sortHotelsByDistance(hotelList, sortOrder);
        }
        break;
      default:
        if (brandBias && brandBiasData?.brand_code) {
          const [brandBiasHotels, nonBrandBiasHotels] =
            filterBrandBiasHotels(hotelList);
          hotelList = [
            ...sortHotelsByDistance(brandBiasHotels, sortOrder),
            ...sortHotelsByDistance(nonBrandBiasHotels, sortOrder),
          ];
        } else {
          hotelList.sort((a, b) => a?.index - b?.index);
        }
    }
    if (hotelList && isDataLayer) {
      //Dev Notes : Implemented a timeout, to ensure that the tracker ready event is available before the product list impressions is triggered.
      setTimeout(() => {
        addProductImpressionGTMDataLayer(hotelList, search);
      }, 2000);
      setIsDataLayer(false);
    }
  }
  return (
    <SearchContainer
      cid={"searchResults"}
      background={props.background ? props.background : "#fff"}
    >
      {props.blockLocationAccess === true ? (
        <LocationAccessPrompt />
      ) : (
        <>
          <SearchHeader
            isLoading={isLoading}
            hotelCount={hotelCount}
            searchString={search.searchString}
            priceFilter={search.priceFilter}
            display={display}
            handleSetDisplay={handleSetDisplay}
            isMobile={isMobile}
          />

          {!isLoading && distanceRadius === 75 && !hotelList.length ? (
            <NoHotelsFound />
          ) : (
            <>
              <SearchFilter
                search={search}
                handlePriceChange={handlePriceChange}
                handleDistanceChange={handleDistanceChange}
                handleAmenitiesChange={handleAmenitiesChange}
                handleBrandsChange={handleBrandsChange}
                crsCodesString={crsCodesString}
                crsCodesBrandsString={crsCodesBrandsString}
                hotelSearchFilters={hotelSearchFilters}
                distanceRadius={distanceRadius}
                brandsInUrl={props.brandsInUrl}
                priceCount={priceCount}
                hotelCount={hotelCount}
                display={display}
                sortBy={sortBy}
                sortOrder={sortOrder}
                background={props.background}
                handleSortChange={handleSortChange}
              />
              {props.unlockBannerShow ? (
                <Row>
                  <Col>
                    <UnlockBanner />
                  </Col>
                </Row>
              ) : null}
              <PromoAlert
                shouldDisplayPromoMessage={shouldDisplayPromoMessage}
                isPromoValid={isPromoValid}
                promotionCode={search.promotionCode}
                groupCode={search.groupCode}
                handlePromoMessageAlert={handlePromoMessageAlert}
              />
              <Row id="search-results" className="search-results">
                {display === "list" && (
                  <SearchList
                    search={search}
                    isPromoValid={isPromoValid || false}
                    hotels={hotelList}
                    isLoading={isLoading}
                    onClickHotelImage={handleClickHotelImage}
                    onRateLoad={onRateLoad}
                    pageType={props.pageType}
                  />
                )}
                {display === "map" && (
                  <SearchMap
                    search={search}
                    isPromoValid={isPromoValid || false}
                    hotels={hotelList}
                    isLoading={isLoading}
                    onClickHotelImage={handleClickHotelImage}
                    onRateLoad={onRateLoad}
                  />
                )}
                {showHotelZoomedImage && (
                  <Lightbox
                    large={hotelImageForZoom.url}
                    alt={hotelImageForZoom.alt}
                    hideZoom={true}
                    hideDownload={true}
                    showRotate={true}
                    onClose={() => {
                      setShowHotelZoomedImage(false);
                    }}
                  />
                )}
              </Row>
            </>
          )}
        </>
      )}
    </SearchContainer>
  );
};
export default SearchResult;
